import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import LeadCard from "@/components/feature/LeadCard";
import { categories, credibilityMap, leads } from "@/mocks/leads";
import { useSeo, BASE_URL } from "@/hooks/useSeo";
import { useScrollReveal } from "@/hooks/useScrollReveal";

export default function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const lead = leads.find((l) => l.id === id);
  useScrollReveal();

  const cat = categories.find((c) => c.id === lead?.category);
  const cred = lead ? credibilityMap[lead.credibility] : null;
  const related = leads.filter((l) => l.id !== id && l.category === lead?.category).slice(0, 3);
  const more = leads.filter((l) => l.id !== id && l.category !== lead?.category).slice(0, 3);
  const relatedList = related.length >= 2 ? related : more;

  useSeo({
    title: lead ? `${lead.title} - 蝌蚪爆料` : "线索详情 - 蝌蚪爆料",
    description: lead
      ? `${lead.summary.slice(0, 110)}`
      : "蝌蚪爆料线索详情，呈现经核实的投稿线索与内幕材料。",
    keywords: lead ? lead.tags.join(",") : "蝌蚪爆料线索",
    path: `/leads/${id ?? ""}`,
    jsonLd: lead
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: lead.title,
            description: lead.summary,
            datePublished: lead.date,
            inLanguage: "zh-CN",
            articleSection: cat?.name,
            keywords: lead.tags.join(","),
            author: { "@type": "Organization", name: "蝌蚪爆料 核实编辑组" },
            publisher: { "@type": "Organization", name: "蝌蚪爆料", url: BASE_URL },
            mainEntityOfPage: `${BASE_URL}/leads/${lead.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: BASE_URL },
              { "@type": "ListItem", position: 2, name: "线索库", item: `${BASE_URL}/leads` },
              { "@type": "ListItem", position: 3, name: lead.title, item: `${BASE_URL}/leads/${lead.id}` },
            ],
          },
        ]
      : undefined,
  });

  if (!lead || !cred) {
    return (
      <section className="mx-auto flex max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-file-unknow-line text-3xl" aria-hidden="true" />
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          没有找到这条线索
        </h1>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
          它可能已被撤回，或者链接输入有误。你可以回到线索库，浏览其他已经完成核实的投稿内容。
        </p>
        <Link
          to="/leads"
          className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line" aria-hidden="true" />
          返回线索库
        </Link>
      </section>
    );
  }

  return (
    <>
      <article>
        <header className="w-full border-b border-background-200 bg-background-100 py-8 md:py-12">
          <div className="mx-auto max-w-[1280px] px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "线索库", to: "/leads" },
                { label: cat?.name ?? "线索", to: `/leads?c=${lead.category}` },
              ]}
            />
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-3 py-1 text-xs font-medium ${cred.className}`}>
                {cred.label}
              </span>
              {lead.tags.map((t) => (
                <span key={t} className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-700">
                  #{t}
                </span>
              ))}
            </div>
            <h1 className="mt-4 max-w-4xl font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              {lead.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-600 md:text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-voice-line text-primary-500" aria-hidden="true" />
                {lead.source}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-primary-500" aria-hidden="true" />
                核实发布日期 {lead.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-timer-line text-primary-500" aria-hidden="true" />
                阅读约 {lead.readTime}
              </span>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <figure className="overflow-hidden rounded-lg border border-background-200">
                <div className="h-56 w-full md:h-80">
                  <img
                    src={lead.cover}
                    alt={`${lead.title} — 线索配图`}
                    title={`${lead.title} 蝌蚪爆料`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <figcaption className="border-t border-background-200 bg-background-100 px-4 py-3 text-xs text-foreground-500">
                  配图为线索相关场景示意，投稿人信息已做匿名处理。
                </figcaption>
              </figure>

              <div className="mt-8 rounded-lg border-l-4 border-primary-500 bg-background-100 p-5 md:p-6">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-list-check-2 text-primary-600" aria-hidden="true" />
                  核实要点
                </h2>
                <ul className="mt-4 space-y-3">
                  {lead.keyPoints.map((p) => (
                    <li key={p} className="flex gap-3 text-sm leading-relaxed text-foreground-700">
                      <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-500" aria-hidden="true" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-8 space-y-5">
                {lead.content.map((para, i) => (
                  <p key={i} className="text-sm leading-loose text-foreground-800 md:text-base">
                    {para}
                  </p>
                ))}
              </div>

              <div className="mt-10 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="font-heading text-base font-bold text-foreground-950">
                    你掌握这条线索的更多细节吗？
                  </p>
                  <p className="mt-1 text-sm text-foreground-600">
                    补充材料会直接交由当条线索的核实编辑，投稿人可选择保持匿名。
                  </p>
                </div>
                <Link
                  to="/guide#submit"
                  className="flex w-fit items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-send-plane-2-line" aria-hidden="true" />
                  补充线索
                </Link>
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-shield-check-line text-secondary-700" aria-hidden="true" />
                  核实状态说明
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                  {lead.credibility === "verified" &&
                    "本条线索已有三个及以上相互独立的来源印证，关键事实经过交叉比对后确认。"}
                  {lead.credibility === "cross" &&
                    "本条线索已获得间接材料或第二来源的支持，部分细节仍在进一步确认中。"}
                  {lead.credibility === "pending" &&
                    "本条线索仍处于核实阶段，目前呈现的是投稿人陈述与可核对材料，结论以最终核实结果为准。"}
                </p>
                <dl className="mt-4 space-y-2 border-t border-secondary-200 pt-4 text-xs">
                  <div className="flex justify-between">
                    <dt className="text-foreground-500">内容栏目</dt>
                    <dd className="font-medium text-foreground-800">{cat?.name}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-foreground-500">来源类型</dt>
                    <dd className="font-medium text-foreground-800">匿名投稿</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-foreground-500">来源保护</dt>
                    <dd className="font-medium text-foreground-800">已开启</dd>
                  </div>
                </dl>
              </div>

              <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">相关线索</h2>
                <ul className="mt-4 space-y-4">
                  {relatedList.map((r) => (
                    <li key={r.id}>
                      <Link to={`/leads/${r.id}`} className="group flex gap-3">
                        <span className="h-14 w-20 shrink-0 overflow-hidden rounded-md">
                          <img
                            src={r.cover}
                            alt={`${r.title} — 相关线索`}
                            title={`${r.title} 蝌蚪爆料`}
                            className="h-full w-full object-cover object-top"
                          />
                        </span>
                        <span className="min-w-0">
                          <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                            {r.title}
                          </span>
                          <span className="mt-1 block text-xs text-foreground-500">{r.date}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 rounded-lg bg-foreground-950 p-5 text-background-100">
                <h2 className="font-heading text-base font-bold text-background-50">投稿人能获得什么</h2>
                <ul className="mt-4 space-y-3 text-sm text-background-300">
                  <li className="flex gap-2.5">
                    <i className="ri-check-line mt-0.5 text-accent-400" aria-hidden="true" />
                    全程匿名，材料加密存储
                  </li>
                  <li className="flex gap-2.5">
                    <i className="ri-check-line mt-0.5 text-accent-400" aria-hidden="true" />
                    发布前逐条确认呈现范围
                  </li>
                  <li className="flex gap-2.5">
                    <i className="ri-check-line mt-0.5 text-accent-400" aria-hidden="true" />
                    可随时要求撤回未发布内容
                  </li>
                </ul>
                <Link
                  to="/guide"
                  className="mt-5 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  了解投稿流程
                </Link>
              </div>
            </aside>
          </div>

          {relatedList.length > 0 && (
            <section className="mt-14 border-t border-background-200 pt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">继续阅读</h2>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {relatedList.map((r) => (
                  <LeadCard key={r.id} lead={r} />
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </>
  );
}